import { MoreHorizontal, MessageSquare } from "lucide-react";

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { TableCell, TableRow } from "@/components/ui/table";
import { Subscriber } from "@/types/subscription";
import { getInitials } from "@/utils";
import { formatSourceName, formatTierName } from "@/utils/subscription/formatUtils";

import WhatsAppVerificationButton from "./WhatsAppVerificationButton";
import SubscriptionActionButtons from "./SubscriptionActionButtons";

interface SubscriberRowProps {
  subscriber: Subscriber;
  processingSubscribers: Record<string, boolean>;
  onSubscriptionChange: (id: string, updates: Partial<Subscriber>) => Promise<void>;
  onGrantAdminSubscription: (subscriber: Subscriber) => void;
}

export default function SubscriberRow({
  subscriber,
  processingSubscribers,
  onSubscriptionChange,
  onGrantAdminSubscription,
}: SubscriberRowProps) {
  const hasRemainingAccess = Boolean(
    (subscriber.expires_at || subscriber.subscription_end) &&
    new Date(subscriber.expires_at || subscriber.subscription_end || '').getTime() > Date.now()
  );

  const needsWhatsAppVerification =
    subscriber.subscribed &&
    (subscriber.tier === "whatsapp_tier" || subscriber.tier === "premium_tier") &&
    subscriber.profile?.whatsapp_number &&
    !subscriber.profile?.whatsapp_verified;

  const statusLabel = (() => {
    switch (subscriber.status) {
      case 'grace_period':
        return 'Grace period';
      case 'requires_action':
        return 'Billing action required';
      case 'past_due':
        return 'Past due';
      case 'canceled':
        return hasRemainingAccess ? 'Scheduled cancellation' : 'Canceled';
      case 'expired':
        return 'Expired';
      case 'scheduled_change':
        return 'Scheduled change';
      case 'trialing':
        return 'Scheduled start';
      case 'active':
        return 'Active';
      default:
        return subscriber.hasSubscriptionRecord === false ? 'No subscription' : 'Inactive';
    }
  })();

  const statusClassName = (() => {
    if (['grace_period', 'requires_action', 'past_due'].includes(subscriber.status || '')) {
      return 'border-amber-200 bg-amber-50 text-amber-800';
    }

    if (subscriber.subscribed) {
      return 'border-emerald-200 bg-emerald-50 text-emerald-700';
    }

    return 'border-gray-200 bg-gray-100 text-gray-600';
  })();

  return (
    <TableRow className="border-gray-200 hover:bg-gray-50">
      <TableCell className="font-medium">
        <div className="flex items-center gap-3">
          <div className="flex-shrink-0">
            {subscriber.profile?.avatar_url ? (
              <Avatar className="h-9 w-9">
                <AvatarImage src={subscriber.profile.avatar_url} alt={subscriber.profile?.first_name || ""} />
                <AvatarFallback>{getInitials(subscriber.profile?.first_name, subscriber.profile?.last_name)}</AvatarFallback>
              </Avatar>
            ) : (
              <Avatar className="h-9 w-9">
                <AvatarFallback>{getInitials(subscriber.profile?.first_name, subscriber.profile?.last_name)}</AvatarFallback>
              </Avatar>
            )}
          </div>

          <div className="min-w-0">
            <div className="truncate text-sm font-medium text-gray-900">
              {subscriber.profile?.first_name || ""} {subscriber.profile?.last_name || ""}
              {!subscriber.profile ? <span className="ml-2 text-xs text-gray-400">(No profile)</span> : null}
            </div>
            <div className="truncate text-sm text-gray-500">{subscriber.email}</div>
            {subscriber.profile?.whatsapp_number ? (
              <div className="mt-1 flex flex-wrap items-center gap-1 text-xs text-gray-500">
                <MessageSquare className="h-3 w-3" />
                {subscriber.profile.whatsapp_number}
                <Badge
                  variant="outline"
                  className={
                    subscriber.profile.whatsapp_verified
                      ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                      : "border-amber-200 bg-amber-50 text-amber-700"
                  }
                >
                  {subscriber.profile.whatsapp_verified ? "Verified" : "Unverified"}
                </Badge>
              </div>
            ) : null}
          </div>
        </div>
      </TableCell>

      <TableCell>
        <Badge variant="outline" className={statusClassName}>
          {statusLabel}
        </Badge>
      </TableCell>

      <TableCell className="text-gray-700">
        {formatTierName(subscriber.tier || subscriber.tier_code || subscriber.subscription_tier || "free_tier")}
      </TableCell>

      <TableCell className="text-gray-500">
        {formatSourceName(subscriber.subscription_source)}
      </TableCell>

      <TableCell className="text-gray-500">
        {subscriber.expires_at || subscriber.subscription_end
          ? new Date(subscriber.expires_at || subscriber.subscription_end || '').toLocaleDateString()
          : '-'}
      </TableCell>

      <TableCell className="text-right">
        <div className="flex flex-wrap items-center justify-end gap-2">
          {needsWhatsAppVerification ? (
            <WhatsAppVerificationButton
              subscriber={subscriber}
              isProcessing={processingSubscribers[subscriber.id]}
            />
          ) : null}

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className="rounded-lg border-gray-200 text-gray-700 hover:border-whatsapp hover:text-whatsapp"
              >
                <MoreHorizontal className="h-4 w-4" />
                Manage
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuLabel>Subscription actions</DropdownMenuLabel>

              {needsWhatsAppVerification ? (
                <>
                  <WhatsAppVerificationButton
                    subscriber={subscriber}
                    isProcessing={processingSubscribers[subscriber.id]}
                    renderAsMenuItem
                  />
                  <DropdownMenuSeparator />
                </>
              ) : null}

              <SubscriptionActionButtons
                subscriber={subscriber}
                onSubscriptionChange={onSubscriptionChange}
                onGrantAdminSubscription={onGrantAdminSubscription}
              />
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </TableCell>
    </TableRow>
  );
}
